import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Interactive Defusion Examples Component
const DefusionInteractive = () => {
  const [revealedThoughts, setRevealedThoughts] = useState({});
  
  const thoughts = [
    { original: "I need to use pornography right now", defused: "I'm having the thought that I need to use pornography right now" },
    { original: "I'm going to fail", defused: "I notice my mind predicting I'm going to fail" },
    { original: "This is unbearable", defused: "I'm having the thought that this is unbearable" }
  ];
  
  return (
    <div className="space-y-3 my-6">
      <p className="text-sm font-medium text-[#ADB5BD] mb-4">Click each thought to see the defused version:</p>
      {thoughts.map((item, idx) => (
        <button
          key={idx}
          onClick={() => setRevealedThoughts(prev => ({ ...prev, [idx]: !prev[idx] }))}
          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${
            revealedThoughts[idx] 
              ? 'bg-[#06D6A0]/20 border-[#06D6A0]' 
              : 'bg-[#3A4550] border-[#4A5568] hover:border-[#00B4D8] hover:shadow-md'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              revealedThoughts[idx] ? 'bg-[#06D6A0]' : 'bg-[#00B4D8]'
            }`}>
              <span className="text-white text-sm">{revealedThoughts[idx] ? '✓' : idx + 1}</span>
            </div>
            <div className="flex-1">
              <p className={`font-medium transition-all ${revealedThoughts[idx] ? 'text-[#ADB5BD] line-through' : 'text-[#F8F9FA]'}`}>
                "{item.original}"
              </p>
              {revealedThoughts[idx] && (
                <p className="text-[#06D6A0] font-medium mt-2 animate-fade-in">
                  → "{item.defused}"
                </p>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

// Thank Mind Interactive Component
const ThankMindInteractive = () => {
  const [revealedResponses, setRevealedResponses] = useState({});
  
  const examples = [
    { thought: "Just this once won't matter", response: "Thanks for the suggestion, mind. I'm choosing differently today." },
    { thought: "You can't handle this urge", response: "I hear you trying to help, mind. I'm going to ride this wave." }
  ];
  
  return (
    <div className="space-y-3 my-6">
      <p className="text-sm font-medium text-[#ADB5BD] mb-4">More examples - click to see the response:</p>
      {examples.map((item, idx) => (
        <button
          key={idx}
          onClick={() => setRevealedResponses(prev => ({ ...prev, [idx]: !prev[idx] }))}
          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${
            revealedResponses[idx] 
              ? 'bg-gradient-to-r from-[#06D6A0]/20 to-[#00B4D8]/20 border-[#06D6A0]' 
              : 'bg-[#3A4550] border-[#4A5568] hover:border-[#00B4D8] hover:shadow-md'
          }`}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">💭</span>
              <p className="font-medium text-[#F8F9FA]">Thought: "{item.thought}"</p>
            </div>
            {revealedResponses[idx] && (
              <div className="flex items-center gap-2 pl-7 animate-fade-in">
                <span className="text-lg">💚</span>
                <p className="text-[#06D6A0] font-medium">"{item.response}"</p>
              </div>
            )}
            {!revealedResponses[idx] && (
              <p className="text-sm text-[#00B4D8] pl-7">Tap to reveal response →</p>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

// Environment Examples Component
const EnvironmentExamples = () => {
  const examples = [
    { icon: "🍳", place: "Kitchen", trigger: "Cookies on the counter", result: "you eat more cookies", better: "Fruit bowl visible", betterResult: "you eat more fruit" },
    { icon: "📱", place: "Phone", trigger: "Social media apps on home screen", result: "you scroll more", better: "Apps in folders", betterResult: "you scroll less" },
    { icon: "🛏️", place: "Bedroom", trigger: "TV in room", result: "worse sleep", better: "Phone charging outside room", betterResult: "better sleep" }
  ];
  
  return (
    <div className="space-y-4 my-6">
      <p className="text-lg font-semibold text-[#F8F9FA]">Examples:</p>
      {examples.map((item, idx) => (
        <div key={idx} className="p-5 bg-gradient-to-r from-[#3A4550] to-[#2D3640] rounded-xl border border-[#4A5568] hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">{item.icon}</span>
            <span className="text-xl font-bold text-[#F8F9FA]">{item.place}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-[#EF476F]/20 rounded-lg border border-[#EF476F]/40">
              <p className="text-sm text-[#F8F9FA]">
                <span className="font-medium">{item.trigger}</span> → {item.result}
              </p>
            </div>
            <div className="p-3 bg-[#06D6A0]/20 rounded-lg border border-[#06D6A0]/40">
              <p className="text-sm text-[#F8F9FA]">
                <span className="font-medium">{item.better}</span> → {item.betterResult}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Ten Second Rule Component
const TenSecondRule = () => {
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-[#00B4D8]/20 to-[#06D6A0]/20 rounded-2xl border-2 border-[#00B4D8]/40">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00B4D8] to-[#06D6A0] flex items-center justify-center shadow-lg">
          <span className="text-2xl font-bold text-white">10</span>
        </div>
        <div>
          <h4 className="text-xl font-bold text-[#F8F9FA]">The 10-Second Rule</h4>
          <p className="text-sm text-[#ADB5BD]">Pause before you click</p>
        </div>
      </div>
      
      <p className="text-base text-[#F8F9FA] mb-4">
        Most relapses happen quickly—an urge arises, you click, you&apos;re in the pattern before you&apos;ve consciously decided.
      </p>
      
      <p className="text-base text-[#F8F9FA] font-medium mb-4">
        Before clicking anything that could lead to the pattern, pause for 10 seconds and ask:
      </p>
      
      <div className="space-y-3">
        {[
          { num: 1, question: "Where is this click taking me?" },
          { num: 2, question: "Is this aligned with who I'm becoming?" },
          { num: 3, question: "What do I actually need right now?" }
        ].map((item) => (
          <div key={item.num} className="flex items-center gap-3 p-3 bg-[#3A4550] rounded-xl border border-[#4A5568]">
            <div className="w-8 h-8 rounded-full bg-[#00B4D8] flex items-center justify-center text-white font-bold text-sm">
              {item.num}
            </div>
            <p className="text-base text-[#F8F9FA] font-medium">&quot;{item.question}&quot;</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Audio Meditation Player Component
const AudioMeditation = ({ title, content }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const audioRef = useRef(null);
  
  // Placeholder audio URL - can be replaced with actual meditation audio
  const audioUrl = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"; // Placeholder
  
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="my-6 p-6 bg-gradient-to-br from-[#06D6A0]/30 to-[#00B4D8]/30 rounded-2xl border-2 border-[#06D6A0]/50">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      {/* Placeholder Notice */}
      <div className="mb-4 p-3 bg-[#FFB627]/30 border border-[#FFB627]/50 rounded-lg">
        <p className="text-xs text-[#F8F9FA]">
          <span className="font-semibold">Note:</span> Placeholder audio is being used. Replace with actual meditation audio.
        </p>
      </div>
      
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={togglePlay}
          className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
            isPlaying 
              ? 'bg-[#06D6A0] hover:bg-[#05C090] scale-110' 
              : 'bg-gradient-to-br from-[#00B4D8] to-[#06D6A0] hover:scale-105'
          }`}
        >
          {isPlaying ? (
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
            </svg>
          ) : (
            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>
        <div className="flex-1">
          <h4 className="text-lg font-bold text-[#F8F9FA]">{title}</h4>
          <p className="text-sm text-[#ADB5BD]">Guided meditation • {formatTime(duration)}</p>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="h-2 bg-[#3A4550] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#06D6A0] transition-all duration-300"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-[#ADB5BD] mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      
      {/* Transcript Toggle */}
      <button
        onClick={() => setShowTranscript(!showTranscript)}
        className="text-sm text-[#00B4D8] hover:text-[#06D6A0] font-medium flex items-center gap-2"
      >
        <svg className={`w-4 h-4 transition-transform ${showTranscript ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        {showTranscript ? 'Hide transcript' : 'Show transcript'}
      </button>
      
      {showTranscript && (
        <div className="mt-4 p-4 bg-[#3A4550] rounded-xl text-sm text-[#F8F9FA] leading-relaxed whitespace-pre-line animate-fade-in">
          {content}
        </div>
      )}
    </div>
  );
};

// Relapse Stages Component (Module 13)
const RelapseStages = () => {
  const [activeStage, setActiveStage] = useState(null);
  
  const stages = [
    { 
      num: 1, 
      title: "Emotional Relapse", 
      color: "#D4A574",
      signs: ["Not attending to self-care", "Isolating", "Bottling up emotions", "Poor sleep, diet, exercise", "Not reaching out for support"],
      intervention: "Focus on self-care and reaching out"
    },
    { 
      num: 2, 
      title: "Mental Relapse", 
      color: "#C4796B",
      signs: ["Thinking about using", "Minimizing consequences", "Bargaining", "Planning how to use without getting caught", "Looking for opportunities"],
      intervention: "Use coping skills, call support, change environment"
    },
    { 
      num: 3, 
      title: "Physical Relapse", 
      color: "#A05A4F",
      signs: ["Actually using pornography", "The behavior itself"],
      intervention: "Stop immediately, reach out, don't spiral"
    }
  ];
  
  return (
    <div className="my-6 space-y-4">
      <p className="text-sm font-medium text-[#ADB5BD] mb-4">Click each stage to learn more:</p>
      <div className="grid grid-cols-1 gap-4">
        {stages.map((stage) => (
          <button
            key={stage.num}
            onClick={() => setActiveStage(activeStage === stage.num ? null : stage.num)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${
              activeStage === stage.num 
                ? 'border-[#00B4D8] bg-[#3A4550]' 
                : 'border-[#4A5568] bg-[#2D3640] hover:border-[#00B4D8]'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: stage.color }}
              >
                {stage.num}
              </div>
              <h4 className="font-bold text-[#F8F9FA]">{stage.title}</h4>
            </div>
            
            {activeStage === stage.num && (
              <div className="mt-4 space-y-3 animate-fade-in">
                <div className="p-3 bg-[#3A4550] rounded-lg border border-[#4A5568]">
                  <p className="text-xs font-medium text-[#ADB5BD] mb-2">Warning Signs:</p>
                  <ul className="space-y-1">
                    {stage.signs.map((sign, idx) => (
                      <li key={idx} className="text-sm text-[#F8F9FA] flex items-start gap-2">
                        <span className="text-[#EF476F]">•</span>
                        {sign}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-3 bg-[#06D6A0]/20 rounded-lg border border-[#06D6A0]/40">
                  <p className="text-xs font-medium text-[#06D6A0] mb-1">Intervention:</p>
                  <p className="text-sm text-[#F8F9FA]">{stage.intervention}</p>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// HALT Check Component (Module 13)
const HALTCheck = () => {
  const [checkedItems, setCheckedItems] = useState({});
  
  const haltItems = [
    { letter: "H", word: "Hungry", desc: "Physical hunger, but also emotional hunger—craving something", icon: "🍽️" },
    { letter: "A", word: "Angry", desc: "Frustration, resentment, irritation—any form of anger", icon: "😤" },
    { letter: "L", word: "Lonely", desc: "Isolated, disconnected, lacking meaningful connection", icon: "💔" },
    { letter: "T", word: "Tired", desc: "Physical exhaustion, but also mental/emotional fatigue", icon: "😴" }
  ];
  
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  
  return (
    <div className="my-6 p-5 bg-gradient-to-br from-[#00B4D8]/20 to-[#06D6A0]/20 rounded-2xl border-2 border-[#00B4D8]/40">
      <h4 className="text-xl font-bold text-[#F8F9FA] mb-4">The HALT Principle</h4>
      <p className="text-sm text-[#ADB5BD] mb-4">Check any that apply to you right now:</p>
      
      <div className="space-y-3">
        {haltItems.map((item) => (
          <button
            key={item.letter}
            onClick={() => setCheckedItems(prev => ({ ...prev, [item.letter]: !prev[item.letter] }))}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
              checkedItems[item.letter]
                ? 'border-[#EF476F] bg-[#EF476F]/20'
                : 'border-[#4A5568] bg-[#3A4550] hover:border-[#00B4D8]'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{item.icon}</span>
              <div className="flex-1">
                <p className="font-bold text-[#F8F9FA]">
                  <span className="text-[#00B4D8]">{item.letter}</span> - {item.word}
                </p>
                <p className="text-xs text-[#ADB5BD]">{item.desc}</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                checkedItems[item.letter] ? 'bg-[#EF476F] border-[#EF476F]' : 'border-[#4A5568]'
              }`}>
                {checkedItems[item.letter] && <span className="text-white text-xs">✓</span>}
              </div>
            </div>
          </button>
        ))}
      </div>
      
      {checkedCount > 0 && (
        <div className={`mt-4 p-3 rounded-lg ${checkedCount >= 2 ? 'bg-[#EF476F]/30 border border-[#EF476F]/50' : 'bg-[#FFB627]/30 border border-[#FFB627]/50'}`}>
          <p className="text-sm font-medium text-[#F8F9FA]">
            {checkedCount >= 2 
              ? `⚠️ ${checkedCount} HALT factors present - High risk! Address these needs before they escalate.`
              : `${checkedCount} HALT factor present - Take care of this need now.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

// Slip Response Protocol Component (Module 13)
const SlipResponseProtocol = () => {
  const [expandedStep, setExpandedStep] = useState(null);
  
  const steps = [
    { 
      time: "Immediately", 
      action: "STOP", 
      details: "Don't continue. Don't binge. One slip doesn't have to become many.",
      icon: "🛑"
    },
    { 
      time: "Within 1 hour", 
      action: "TELL SOMEONE", 
      details: "Contact your accountability partner or support person. Break the secrecy immediately.",
      icon: "📞"
    },
    { 
      time: "Within 24 hours", 
      action: "ANALYZE", 
      details: "What happened? What led to it? What can you learn? Don't shame—analyze.",
      icon: "🔍"
    },
    { 
      time: "Within 48 hours", 
      action: "ADJUST", 
      details: "Update your plan based on what you learned. Add support, change environment, strengthen skills.",
      icon: "📝"
    },
    { 
      time: "Ongoing", 
      action: "RETURN", 
      details: "Get back to your recovery practices immediately. Don't wait for a 'fresh start' date.",
      icon: "🔄"
    }
  ];
  
  return (
    <div className="my-6">
      <h4 className="text-lg font-bold text-[#F8F9FA] mb-4">The First 24-48 Hours Protocol</h4>
      <div className="space-y-3">
        {steps.map((step, idx) => (
          <button
            key={idx}
            onClick={() => setExpandedStep(expandedStep === idx ? null : idx)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
              expandedStep === idx
                ? 'border-[#06D6A0] bg-[#06D6A0]/10'
                : 'border-[#4A5568] bg-[#3A4550] hover:border-[#00B4D8]'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{step.icon}</span>
              <div className="flex-1">
                <p className="text-xs text-[#00B4D8] font-medium">{step.time}</p>
                <p className="font-bold text-[#F8F9FA]">{step.action}</p>
              </div>
              <svg className={`w-5 h-5 text-[#00B4D8] transition-transform ${expandedStep === idx ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {expandedStep === idx && (
              <p className="mt-3 text-sm text-[#ADB5BD] pl-11 animate-fade-in">{step.details}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default function ModuleViewer() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reflections, setReflections] = useState({});
  const [savingReflection, setSavingReflection] = useState(false);
  const [completedSections, setCompletedSections] = useState({
    content: false,
    example: false,
    reflections: false,
    action: false
  });
  const [showContent, setShowContent] = useState(false);
  const [revealedNeeds, setRevealedNeeds] = useState({});

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  useEffect(() => {
    // Trigger content animation after load
    if (module) {
      setTimeout(() => setShowContent(true), 100);
    }
  }, [currentPage, module]);

  const fetchModule = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/modules/${moduleId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setModule(data);
        setCurrentPage(data.progress?.current_page || 1);
      } else if (response.status === 403) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReflectionChange = (questionIndex, value) => {
    setReflections(prev => ({
      ...prev,
      [`page_${currentPage}_q${questionIndex}`]: value
    }));
    
    // Check if all reflections are filled
    const currentPageData = module.pages[currentPage - 1];
    if (currentPageData.reflection_questions) {
      const allFilled = currentPageData.reflection_questions.every((_, idx) => {
        const key = `page_${currentPage}_q${idx}`;
        return reflections[key]?.length > 10 || (idx === questionIndex && value.length > 10);
      });
      
      if (allFilled) {
        setCompletedSections(prev => ({ ...prev, reflections: true }));
      }
    }
  };

  const saveReflection = async (questionIndex, value) => {
    setSavingReflection(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Error saving reflection:', error);
    } finally {
      setSavingReflection(false);
    }
  };

  const toggleSection = (section) => {
    setCompletedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNextPage = async () => {
    if (currentPage < module.pages.length) {
      const nextPage = currentPage + 1;
      setShowContent(false);
      setTimeout(() => {
        setCurrentPage(nextPage);
        setCompletedSections({
          content: false,
          example: false,
          reflections: false,
          action: false
        });
        setRevealedNeeds({});
      }, 300);
      
      await fetch(`${BACKEND_URL}/api/modules/${moduleId}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_page: nextPage })
      });
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setShowContent(false);
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
      }, 300);
    }
  };

  const handleMarkComplete = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/modules/${moduleId}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mark_complete: true })
      });

      navigate('/dashboard', {
        state: {
          moduleCompleted: {
            moduleId: moduleId,
            moduleNumber: module.number
          }
        }
      });
    } catch (error) {
      console.error('Error marking complete:', error);
    }
  };

  const formatContent = (content) => {
    // Check for relapse stages (Module 13)
    if (content.includes('[RELAPSE_STAGES]')) {
      const parts = content.split('[RELAPSE_STAGES]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <RelapseStages />
          {formatRegularContent(parts[1])}
        </>
      );
    }
    
    // Check for HALT check (Module 13)
    if (content.includes('[HALT_CHECK]')) {
      const parts = content.split('[HALT_CHECK]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <HALTCheck />
          {formatRegularContent(parts[1])}
        </>
      );
    }
    
    // Check for slip response protocol (Module 13)
    if (content.includes('[SLIP_RESPONSE_PROTOCOL]')) {
      const parts = content.split('[SLIP_RESPONSE_PROTOCOL]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <SlipResponseProtocol />
          {formatRegularContent(parts[1])}
        </>
      );
    }
    
    // Check for environment examples
    if (content.includes('[ENVIRONMENT_EXAMPLES]')) {
      const parts = content.split('[ENVIRONMENT_EXAMPLES]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <EnvironmentExamples />
          {formatRegularContent(parts[1])}
        </>
      );
    }
    
    // Check for 10-second rule
    if (content.includes('[TEN_SECOND_RULE]')) {
      const parts = content.split('[TEN_SECOND_RULE]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <TenSecondRule />
          {formatRegularContent(parts[1])}
        </>
      );
    }
    
    // Check for defusion interactive
    if (content.includes('[DEFUSION_INTERACTIVE]')) {
      const parts = content.split('[DEFUSION_INTERACTIVE]');
      const afterParts = parts[1].split('[THANK_MIND_INTERACTIVE]');
      return (
        <>
          {formatRegularContent(parts[0])}
          <DefusionInteractive />
          {formatRegularContent(afterParts[0])}
          {afterParts[1] && <ThankMindInteractive />}
          {afterParts[1] && formatRegularContent(afterParts[1])}
        </>
      );
    }
    
    // Check if content contains interactive needs section
    if (content.includes('INTERACTIVE_NEEDS:')) {
      const parts = content.split('INTERACTIVE_NEEDS:');
      const beforeNeeds = parts[0];
      const afterParts = parts[1].split('\n\n');
      const needsData = afterParts[0].trim();
      const afterNeeds = afterParts.slice(1).join('\n\n');
      
      // Parse needs data
      const needs = needsData.split('\n').map(line => {
        const [feeling, need, solution] = line.split('|');
        return { feeling, need, solution };
      }).filter(n => n.feeling);
      
      return (
        <>
          {formatRegularContent(beforeNeeds)}
          
          {/* Interactive Needs Section */}
          <div className="space-y-3 my-6">
            {needs.map((item, idx) => {
              const isRevealed = revealedNeeds[`need_${idx}`];
              
              return (
                <div 
                  key={idx}
                  className="border-2 border-[#4A5568] rounded-xl overflow-hidden hover:border-[#00B4D8]/30 transition-all duration-300"
                >
                  {/* Feeling & Need */}
                  <div className="p-4 bg-gradient-to-r from-[#3A4550] to-[#2D3640]">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{['😔', '😰', '😑', '😟', '😢', '😠'][idx % 6]}</span>
                      <div className="flex-1">
                        <p className="font-bold text-[#F8F9FA] text-lg">{item.feeling}</p>
                        <p className="text-sm text-[#ADB5BD]">Need: <span className="text-[#00B4D8] font-medium">{item.need}</span></p>
                      </div>
                    </div>
                    
                    {/* Toggle Button */}
                    <button
                      onClick={() => setRevealedNeeds(prev => ({ ...prev, [`need_${idx}`]: !prev[`need_${idx}`] }))}
                      className={`w-full mt-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-between ${
                        isRevealed
                          ? 'bg-[#06D6A0] text-white shadow-md'
                          : 'bg-[#00B4D8] text-white hover:bg-[#0094B8] shadow-sm'
                      }`}
                    >
                      <span>{isRevealed ? 'Hide solutions' : 'How to meet it →'}</span>
                      <svg 
                        className={`w-5 h-5 transition-transform duration-300 ${isRevealed ? 'rotate-180' : ''}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Solution (Revealed) */}
                  {isRevealed && (
                    <div className="p-4 bg-[#06D6A0]/20 border-t-2 border-[#06D6A0]/30 animate-slide-down">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#06D6A0] flex items-center justify-center flex-shrink-0 mt-1">
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-base text-[#F8F9FA] leading-relaxed flex-1">{item.solution}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {formatRegularContent(afterNeeds)}
        </>
      );
    }
    
    return formatRegularContent(content);
  };
  
  const formatRegularContent = (content) => {
    const paragraphs = content.split('\n\n');
    
    // Bold masculine accent colors
    const accentColors = [
      { bg: 'bg-[#00B4D8]/10', border: 'border-[#00B4D8]/30', dot: 'bg-[#00B4D8]', text: 'text-[#00B4D8]' },
      { bg: 'bg-[#FF6B35]/10', border: 'border-[#FF6B35]/30', dot: 'bg-[#FF6B35]', text: 'text-[#FF6B35]' },
      { bg: 'bg-[#06D6A0]/10', border: 'border-[#06D6A0]/30', dot: 'bg-[#06D6A0]', text: 'text-[#06D6A0]' },
      { bg: 'bg-[#FFB627]/10', border: 'border-[#FFB627]/30', dot: 'bg-[#FFB627]', text: 'text-[#FFB627]' },
      { bg: 'bg-[#EF476F]/10', border: 'border-[#EF476F]/30', dot: 'bg-[#EF476F]', text: 'text-[#EF476F]' },
    ];
    
    return paragraphs.map((para, idx) => {
      // Check for section headers (lines ending with :)
      if (para.includes(':') && para.split(':')[0].length < 50 && !para.includes('\n')) {
        const [title, ...rest] = para.split(':');
        const content = rest.join(':').trim();
        if (content.length < 200 && title.length < 40) {
          const colorSet = accentColors[idx % accentColors.length];
          return (
            <div key={idx} className={`p-4 rounded-xl ${colorSet.bg} border ${colorSet.border} my-4`}>
              <p className={`font-bold text-lg mb-1 ${colorSet.text}`}>{title}:</p>
              {content && <p className="text-[#F8F9FA]">{content}</p>}
            </div>
          );
        }
      }
      
      // Check for bullet lists
      if (para.includes('\n•') || para.includes('\n-')) {
        const lines = para.split('\n');
        const title = lines[0];
        const items = lines.slice(1).filter(l => l.trim());
        const colorSet = accentColors[idx % accentColors.length];
        
        return (
          <div key={idx} className="space-y-3 my-4">
            {title && !title.startsWith('•') && !title.startsWith('-') && (
              <p className="text-lg font-bold text-white flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${colorSet.dot}`}></span>
                {title}
              </p>
            )}
            <div className="grid gap-3">
              {items.map((item, i) => {
                const cleanItem = item.replace(/^[•\-]\s*/, '').trim();
                if (!cleanItem) return null;
                const itemColor = accentColors[(idx + i) % accentColors.length];
                
                return (
                  <div 
                    key={i} 
                    className={`flex items-start gap-3 p-4 ${itemColor.bg} rounded-xl border ${itemColor.border} hover:scale-[1.02] transition-all duration-300`}
                  >
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${itemColor.dot}`} />
                    <p className="text-[#F8F9FA] leading-relaxed flex-1">{cleanItem}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      
      // Check for key insight or important text
      if (para.toLowerCase().includes('key insight') || para.toLowerCase().includes('important') || para.toLowerCase().includes('remember:')) {
        return (
          <div key={idx} className="p-5 bg-gradient-to-r from-[#FFB627]/20 to-[#FF6B35]/20 rounded-xl border-l-4 border-[#FFB627] my-4">
            <p className="text-[#F8F9FA] leading-relaxed font-medium">{para}</p>
          </div>
        );
      }
      
      // Check for "why this works" or explanatory sections
      if (para.toLowerCase().includes('why this works') || para.toLowerCase().includes('the science')) {
        return (
          <div key={idx} className="p-5 bg-gradient-to-r from-[#00B4D8]/20 to-[#06D6A0]/20 rounded-xl border-l-4 border-[#00B4D8] my-4">
            <p className="text-[#F8F9FA] leading-relaxed">{para}</p>
          </div>
        );
      }
      
      // Regular paragraphs with better spacing
      return (
        <p key={idx} className="text-[#ADB5BD] leading-relaxed text-lg">
          {para}
        </p>
      );
    });
  };

  const getPageCompletionPercentage = () => {
    const sections = Object.keys(completedSections);
    const completed = sections.filter(key => completedSections[key]).length;
    return (completed / sections.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C2127] pb-20">
        <Navigation />
        <main className="max-w-2xl mx-auto px-4 py-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
            <p className="text-[#ADB5BD] mt-4">Loading module...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[#1C2127] pb-20">
        <Navigation />
        <main className="max-w-2xl mx-auto px-4 py-6">
          <div className="text-center py-12">
            <p className="text-[#ADB5BD]">Module not found</p>
          </div>
        </main>
      </div>
    );
  }

  const currentPageData = module.pages[currentPage - 1];
  const isLastPage = currentPage === module.pages.length;
  const pageCompletion = getPageCompletionPercentage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1C2127] via-[#2D3640] to-[#1C2127] pb-20">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className={`space-y-3 transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#00B4D8] hover:text-[#06D6A0] transition-colors group"
          >
            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00B4D8] via-[#06D6A0] to-[#00B4D8] flex items-center justify-center text-white font-bold text-xl shadow-lg animate-pulse">
                {module.number}
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#06D6A0] rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                ✓
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-[#F8F9FA] mb-1">{module.title}</h1>
              <p className="text-sm text-[#ADB5BD]">{module.description}</p>
            </div>
          </div>
        </div>

        {/* Progress indicators */}
        <div className={`space-y-3 transition-all duration-500 delay-100 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          {/* Page progress */}
          <div className="flex items-center gap-2">
            {module.pages.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-all duration-500 ${
                  index < currentPage
                    ? 'bg-[#00B4D8] shadow-sm'
                    : index === currentPage - 1
                    ? 'bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] shadow-md'
                    : 'bg-[#4A5568]'
                }`}
              />
            ))}
          </div>
          
          {/* Page completion */}
          {pageCompletion > 0 && (
            <div className="bg-[#2D3640] rounded-xl p-3 border border-[#4A5568] shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#ADB5BD]">Page Progress</span>
                <span className="text-xs font-bold text-[#00B4D8]">{Math.round(pageCompletion)}%</span>
              </div>
              <div className="h-1.5 bg-[#4A5568] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] transition-all duration-500 rounded-full"
                  style={{ width: `${pageCompletion}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content Card */}
        <div className={`bg-[#2D3640] rounded-3xl border border-[#4A5568] shadow-xl overflow-hidden transition-all duration-500 delay-200 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Image */}
          {currentPageData.image_url && (
            <div className="relative overflow-hidden">
              <img
                src={currentPageData.image_url}
                alt={currentPageData.title}
                className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          )}
          
          <div className="p-8 space-y-8">
            {/* Page Title */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-[#F8F9FA] to-[#00B4D8] bg-clip-text text-transparent">
                  {currentPageData.title}
                </h2>
                <span className="text-sm text-[#ADB5BD] bg-[#3A4550] px-4 py-2 rounded-full font-medium">
                  {currentPage} of {module.pages.length}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-5">
              {formatContent(currentPageData.content)}
            </div>

            {/* Example Section */}
            {currentPageData.example && (
              currentPageData.example.includes('[AUDIO_MEDITATION]') ? (
                // Audio Meditation Player
                <AudioMeditation 
                  title="Leaves on a Stream Meditation"
                  content={currentPageData.example.replace('[AUDIO_MEDITATION]\n', '').replace('[AUDIO_MEDITATION]', '')}
                />
              ) : (
                <div className="mt-8 relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#06D6A0]/20 to-[#00B4D8]/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
                  <div className="relative p-6 bg-gradient-to-br from-[#3A4550] via-[#2D3640] to-[#3A4550] rounded-2xl border-2 border-[#06D6A0]/30 hover:border-[#06D6A0] transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#06D6A0] to-[#00B4D8] flex items-center justify-center shadow-lg transform group-hover:rotate-12 transition-transform duration-300">
                        <span className="text-2xl">💡</span>
                      </div>
                      <p className="text-lg font-bold text-[#F8F9FA]">Try This:</p>
                    </div>
                    <div className="space-y-3 text-base text-[#F8F9FA] leading-relaxed pl-13">
                      {currentPageData.example.split('\n\n').map((para, idx) => (
                        <p key={idx} className="whitespace-pre-line">{para}</p>
                      ))}
                    </div>
                    <button
                      onClick={() => toggleSection('example')}
                      className={`mt-4 text-sm font-medium transition-colors ${
                        completedSections.example ? 'text-[#06D6A0]' : 'text-[#00B4D8] hover:text-[#06D6A0]'
                      }`}
                    >
                      {completedSections.example ? '✓ Got it!' : 'Mark as understood'}
                    </button>
                  </div>
                </div>
              )
            )}

            {/* Reflection Questions */}
            {currentPageData.reflection_questions && currentPageData.reflection_questions.length > 0 && (
              <div className="mt-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00B4D8] to-[#06D6A0] flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#F8F9FA]">Your Reflections</h3>
                </div>
                
                <div className="space-y-5">
                  {currentPageData.reflection_questions.map((question, index) => {
                    const key = `page_${currentPage}_q${index}`;
                    const hasContent = reflections[key]?.length > 0;
                    
                    return (
                      <div key={index} className={`space-y-3 p-5 rounded-2xl border-2 transition-all duration-300 ${
                        hasContent 
                          ? 'bg-gradient-to-br from-[#06D6A0]/20 to-transparent border-[#06D6A0]/50' 
                          : 'bg-[#3A4550] border-[#4A5568] hover:border-[#00B4D8]/30'
                      }`}>
                        <label className="flex items-start gap-3">
                          <span className="text-2xl mt-1">{['🤔', '💭', '✨', '🌱'][index % 4]}</span>
                          <span className="block text-base font-medium text-[#F8F9FA] flex-1">
                            {question}
                          </span>
                        </label>
                        <textarea
                          value={reflections[key] || ''}
                          onChange={(e) => handleReflectionChange(index, e.target.value)}
                          onBlur={(e) => saveReflection(index, e.target.value)}
                          placeholder="Take your time... there's no wrong answer"
                          className="w-full min-h-[120px] p-4 border-2 border-[#4A5568] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent resize-y text-[#F8F9FA] placeholder-[#6B7280] bg-[#2D3640] backdrop-blur-sm transition-all duration-300"
                        />
                        {hasContent && (
                          <div className="flex items-center gap-2 text-sm text-[#06D6A0]">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Saved
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Step */}
            {currentPageData.action_step && (
              <div className="mt-8 relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-[#00B4D8]/20 to-[#06D6A0]/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
                <div className="relative p-6 bg-gradient-to-br from-[#06D6A0]/10 via-[#3A4550] to-transparent rounded-2xl border-2 border-[#06D6A0]/30 hover:border-[#06D6A0] transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#06D6A0] to-[#00B4D8] flex items-center justify-center flex-shrink-0 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="flex-1 space-y-3">
                      <p className="text-lg font-bold text-[#F8F9FA]">Your Action Step:</p>
                      <p className="text-base text-[#F8F9FA] leading-relaxed">{currentPageData.action_step}</p>
                      <button
                        onClick={() => toggleSection('action')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          completedSections.action
                            ? 'bg-[#06D6A0] text-white shadow-md'
                            : 'bg-[#3A4550] text-[#00B4D8] border-2 border-[#00B4D8] hover:bg-[#00B4D8] hover:text-white'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          {completedSections.action ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          )}
                        </svg>
                        {completedSections.action ? 'Committed!' : 'I commit to this'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className={`flex items-center justify-between gap-4 transition-all duration-500 delay-300 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            variant="outline"
            className="rounded-full px-6 py-5 h-auto text-base font-medium border-2 border-[#4A5568] bg-[#2D3640] text-[#ADB5BD] hover:bg-[#3A4550] hover:border-[#00B4D8] hover:text-[#00B4D8] disabled:opacity-50 transition-all duration-300 group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </Button>

          {isLastPage ? (
            <Button
              onClick={handleMarkComplete}
              className="rounded-full px-8 py-5 h-auto text-base font-semibold bg-gradient-to-r from-[#00B4D8] via-[#06D6A0] to-[#00B4D8] hover:shadow-2xl text-white shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              Complete Module
              <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </Button>
          ) : (
            <Button
              onClick={handleNextPage}
              className="rounded-full px-8 py-5 h-auto text-base font-semibold bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#0094B8] hover:to-[#05B680] text-white shadow-lg transform hover:scale-105 transition-all duration-300 group"
            >
              Continue
              <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
